import React, { Component } from 'react';
import './TemperatureSensorGraphCard.css';
import Card from '../../components/card/Card';
import CardHeader from '../../components/cardHeader/CardHeader';
import Label from '../../components/label/Label';
import Chart from 'chart.js';
import mainAPI from '../../api/MainAPI';
import utils from '../../utils/utils';
import models from "../../model/models";

export default class TemperatureSensorGraphCard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			readings: [],
			loading: true,
			error: null,
			high24h: null,
			low24h: null,
			highAllTime: null,
			lowAllTime: null
		};
		this.chart = null;
	}

	componentDidMount = () => {
		this._getSensorData(this.props);
	};
	componentWillReceiveProps(nextProps) {
		const oldBox = this.props.drugBox || {};
		const newBox = nextProps.drugBox || {};
		if(oldBox.sensor_id !== newBox.sensor_id) {
			this._getSensorData(nextProps);
		}
	}

	componentWillUnmount = () => {
		if (this.chart) {
			this.chart.destroy();
		}
	};

	_getSensorData = async (props) => {
		const box = props.drugBox || {};
		const drugBoxId = box.id;
		if(!drugBoxId) return;
		if(!box.sensor_id) return;
		try {
			const endTime = new Date();
			const startTime = new Date();
			startTime.setHours(startTime.getHours() - 24);

			const [readings, stats] = await Promise.all([
				mainAPI.sensor_reading.forDrugBox(drugBoxId, {
					start_time: startTime.toISOString(),
					end_time: endTime.toISOString(),
					limit: 2000
				}),
				mainAPI.sensor_reading.statsForDrugBox(drugBoxId)
			]);

			this.setState({
				readings, 
				allTimeHigh: stats.all_time_high_c,
				allTimeLow: stats.all_time_low_c,
				loading: false 
			}, () => {
				this._calculateStats();
				this._createChart();
			});
		} catch (error) {
			console.error('Error fetching sensor data:', error);
			this.setState({ loading: false, error: 'Failed to load sensor data' });
		}
	};

	_calculateStats = () => {
		const { readings } = this.state;
		if (!readings) return;
		if (!readings.length) return;

		const temperatures = readings.map(r => r.temperature_c).filter(t => t !== null);
		if (!temperatures) return;
		if (!temperatures.length) return;

		const high24h = Math.max(...temperatures);
		const low24h = Math.min(...temperatures);

		this.setState({ high24h, low24h });

	};

	_roundToNearestMinute = (timestamp) => {
		const date = new Date(timestamp);
		date.setSeconds(0, 0);
		return date;
	};

	_detectOptimalTimeStep = (readings) => {
		if (!readings || readings.length < 2) {
			return 15 * 60 * 1000; // Default to 15 minutes
		}

		// Calculate average time between readings
		const timeDiffs = [];
		for (let i = 1; i < readings.length; i++) {
			const prevTime = this._roundToNearestMinute(readings[i - 1].observed_on);
			const currTime = this._roundToNearestMinute(readings[i].observed_on);
			timeDiffs.push(currTime.getTime() - prevTime.getTime());
		}

		const avgTimeDiff = timeDiffs.reduce((sum, diff) => sum + diff, 0) / timeDiffs.length;
		
		// Determine optimal time step based on data frequency
		if (avgTimeDiff < 5 * 60 * 1000) { // Less than 5 minutes between readings
			return 5 * 60 * 1000; // 5-minute intervals
		} else if (avgTimeDiff < 15 * 60 * 1000) { // Less than 15 minutes between readings
			return 15 * 60 * 1000; // 15-minute intervals
		} else if (avgTimeDiff < 60 * 60 * 1000) { // Less than 1 hour between readings
			return 30 * 60 * 1000; // 30-minute intervals
		} else {
			return 60 * 60 * 1000; // 1-hour intervals
		}
	};

	_createChart = () => {
		const box = this.props.drugBox || {};
		const drugBoxId = box.id;
		const { readings } = this.state;
		const ctx = document.getElementById(`temperature-chart-${drugBoxId}`);
		if (!ctx) return;

		if (this.chart) {
			this.chart.destroy();
		}

		// Create 24-hour time range
		const now = new Date();
		const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
		
		// Filter readings within the last 24 hours
		const sortedReadings = readings
			.filter(r => {
				const readingTime = this._roundToNearestMinute(r.observed_on);
				return r.temperature_c !== null && readingTime >= twentyFourHoursAgo;
			})
			.sort((a, b) => this._roundToNearestMinute(a.observed_on) - this._roundToNearestMinute(b.observed_on));

		// Use smaller time steps to capture more data points
		const timeStep = 1 * 60 * 1000; // 5-minute intervals for better granularity
		const totalSteps = (24 * 60 * 60 * 1000) / timeStep;
		
		const labels = [];
		const data = [];
		
		// Generate evenly spaced time points with smaller tolerance
		const tolerance = timeStep / 4; // Quarter the time step for tighter tolerance
		for (let i = 0; i < totalSteps; i++) {
			const time = this._roundToNearestMinute(twentyFourHoursAgo.getTime() + i * timeStep);
			labels.push(utils.display_time(utils.server_to_client_datetime(time)));
			
			// Find the closest reading within tolerance of this time point
			const closestReading = sortedReadings.find(reading => {
				const roundedTime = this._roundToNearestMinute(reading.observed_on);
				const timeDiff = Math.abs(roundedTime.getTime() - time.getTime());
				return timeDiff <= tolerance;
			});
			
			data.push(closestReading ? closestReading.temperature_c : null);
		}

		this.chart = new Chart(ctx, {
			type: 'line',
			data: {
				labels,
				datasets: [{
					label: 'Temperature °C',
					data,
					borderColor: '#0086C9',
					backgroundColor: 'rgba(0, 134, 201, 0.1)',
					borderWidth: 1,
					fill: true,
					tension: 0.1,
					pointRadius: 1,
					pointHoverRadius: 3,
					spanGaps: true
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				interaction: {
					intersect: false,
					mode: 'index'
				},
				scales: {
					y: {
						beginAtZero: false,
						title: {
							display: true,
							text: 'Temperature °C'
						}
					},
					x: {
						title: {
							display: true,
							text: 'Time (Last 24 Hours)'
						},
						ticks: {
							maxTicksLimit: 12,
							callback: function(value, index, ticks) {
								return this.getLabelForValue(value);
							}
						},
						type: 'category'
					}
				},
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						callbacks: {
							label: (context) => {
								if (context.parsed.y === null) {
									return 'No data available';
								}
								return `Temperature: ${context.parsed.y.toFixed(1)}°C`;
							}
						}
					}
				},
				elements: {
					line: {
						spanGaps: true
					}
				}
			}
		});
	};

	render() {
		const { loading, error, high24h, low24h, allTimeHigh, allTimeLow } = this.state;
		const drug_box = this.props.drugBox || {};
		if(!this.props.drugBox) return null
		if(!drug_box.sensor_id) return null;
		if (!this.state.readings) {
			return (
				<Card className={'g-temperature-sensor-graph-card'}>
					<CardHeader>Temperature Monitoring</CardHeader>
					<Label>No temperature sensor associated with this drug box</Label>
				</Card>
			);
		}

		if (loading) {
			return (
				<Card className={'g-temperature-sensor-graph-card'}>
					<CardHeader>Temperature Monitoring</CardHeader>
					<Label>Loading temperature data...</Label>
				</Card>
			);
		}

		if (error) {
			return (
				<Card className={'g-temperature-sensor-graph-card'}>
					<CardHeader>Temperature Monitoring</CardHeader>
					<Label red>{error}</Label>
				</Card>
			);
		}

		return (
			<Card className={'g-temperature-sensor-graph-card'}>
				<CardHeader>Temperature Monitoring</CardHeader>
				<div className="temperature-stats">
					<div className="stat-group">
						<Label black bold>Last 24 Hours</Label>
						<div className="stats-row">
							<div className="stat">
								<Label>High: {high24h ? `${high24h.toFixed(1)}°C` : 'N/A'}</Label>
							</div>
							<div className="stat">
								<Label>Low: {low24h ? `${low24h.toFixed(1)}°C` : 'N/A'}</Label>
							</div>
						</div>
					</div>
					<div className="stat-group">
						<Label black bold>All Time</Label>
						<div className="stats-row">
							<div className="stat">
								<Label>High: {allTimeHigh ? `${allTimeHigh.toFixed(1)}°C` : 'N/A'}</Label>
							</div>
							<div className="stat">
								<Label>Low: {allTimeLow ? `${allTimeLow.toFixed(1)}°C` : 'N/A'}</Label>
							</div>
						</div>
					</div>
				</div>
				<div className="temperature-chart-container">
					<canvas id={`temperature-chart-${drug_box.id}`}></canvas>
				</div>
			</Card>
		);
	}
}



// WEBPACK FOOTER //
// ./src/cards/temperatureSensorGraphCard/TemperatureSensorGraphCard.jsx